/* motion.js — Dandy 1970
 * Lenis smooth scroll + GSAP scroll-triggered reveals + headline word reveal.
 * Respects prefers-reduced-motion.
 */
(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const settings = (window.Dandy && window.Dandy.settings) || {};
  const supportsIO = ('IntersectionObserver' in window);

  // ------- Lenis smooth scroll
  function initLenis() {
    if (reduceMotion || !settings.smooth_scroll || typeof window.Lenis === 'undefined') return;
    const lenis = new window.Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      smoothTouch: false,
    });
    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
    window.lenis = lenis;
    if (window.gsap && window.ScrollTrigger) {
      lenis.on('scroll', window.ScrollTrigger.update);
      window.gsap.ticker.add((time) => lenis.raf(time * 1000));
      window.gsap.ticker.lagSmoothing(0);
    }
  }

  // ------- Reveal on scroll
  function initReveal() {
    const els = document.querySelectorAll('.reveal, [data-reveal]');
    if (!els.length) return;
    if (reduceMotion || !supportsIO) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );
    els.forEach((el) => io.observe(el));
  }

  // ------- Word-by-word headline split (manual SplitText alternative)
  function splitHeadline(el) {
    if (!el || el.dataset.split === 'done') return;
    const text = el.textContent.trim();
    el.textContent = '';
    text.split(' ').forEach((word) => {
      const w = document.createElement('span');
      w.className = 'word';
      const inner = document.createElement('span');
      inner.textContent = word + ' ';
      w.appendChild(inner);
      el.appendChild(w);
    });
    el.dataset.split = 'done';
  }

  function initHeadline() {
    document.querySelectorAll('[data-split-words]').forEach((el) => {
      splitHeadline(el);
      // Trigger reveal when in view
      if (reduceMotion || !supportsIO) {
        el.classList.add('is-in');
        return;
      }
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              el.classList.add('is-in');
              io.disconnect();
            }
          });
        },
        { threshold: 0.2 }
      );
      io.observe(el);
    });
  }

  // ------- Header tint on scroll
  function initHeader() {
    const header = document.querySelector('[data-site-header]');
    if (!header) return;
    const threshold = window.innerHeight * 0.8;
    const onScroll = () => {
      header.classList.toggle('is-scrolled', window.scrollY > threshold);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // ------- Parallax for heritage strip
  function initParallax() {
    if (reduceMotion) return;
    const els = document.querySelectorAll('[data-parallax]');
    if (!els.length) return;
    const onScroll = () => {
      els.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const inView = rect.top < window.innerHeight && rect.bottom > 0;
        if (!inView) return;
        const progress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
        const y = (progress - 0.5) * 60; // ~12% range
        const media = el.querySelector('[data-parallax-media]') || el;
        media.style.transform = `translate3d(0, ${y}px, 0)`;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // ------- Embla editorial reel
  function initReel() {
    const viewport = document.querySelector('[data-reel-viewport]');
    if (!viewport || typeof window.EmblaCarousel === 'undefined') return;
    const embla = window.EmblaCarousel(viewport, {
      loop: false,
      align: 'start',
      dragFree: true,
      containScroll: 'trimSnaps',
    });
    const prev = document.querySelector('[data-reel-prev]');
    const next = document.querySelector('[data-reel-next]');
    if (prev) prev.addEventListener('click', () => embla.scrollPrev());
    if (next) next.addEventListener('click', () => embla.scrollNext());
    // Autoplay videos when in view
    const slides = viewport.querySelectorAll('video');
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const v = entry.target;
            if (entry.isIntersecting) {
              v.play().catch(() => {});
            } else {
              v.pause();
            }
          });
        },
        { threshold: 0.4 }
      );
      slides.forEach((s) => io.observe(s));
    }
  }

  // ------- Hero video lazy load
  function initHeroVideo() {
    const heroes = document.querySelectorAll('[data-hero-video]');
    heroes.forEach((wrap) => {
      const video = wrap.querySelector('video');
      if (!video) return;
      const sources = video.querySelectorAll('source[data-src]');
      const conn = navigator.connection;
      const slow = conn && (conn.saveData || /(2g|slow-2g)/i.test(conn.effectiveType || ''));
      if (slow) return; // keep poster only
      if (!('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              sources.forEach((s) => {
                s.src = s.dataset.src;
              });
              video.load();
              video.play().catch(() => {});
              io.disconnect();
            }
          });
        },
        { threshold: 0.1 }
      );
      io.observe(wrap);
    });
  }

  // ------- Mobile menu toggle (right drawer + dim backdrop)
  function initMobileMenu() {
    const btn = document.querySelector('[data-mobile-menu-open]');
    const menu = document.querySelector('[data-mobile-menu]');
    const backdrop = document.querySelector('[data-mobile-menu-backdrop]');
    if (!btn || !menu) return;
    const open = () => {
      menu.classList.add('is-open');
      if (backdrop) backdrop.classList.add('is-on');
      document.body.style.overflow = 'hidden';
    };
    const close = () => {
      menu.classList.remove('is-open');
      if (backdrop) backdrop.classList.remove('is-on');
      document.body.style.overflow = '';
    };
    btn.addEventListener('click', open);
    document.querySelectorAll('[data-mobile-menu-close]').forEach((el) => {
      el.addEventListener('click', close);
    });
    if (backdrop) backdrop.addEventListener('click', close);
    menu.querySelectorAll('a').forEach((a) => {
      a.addEventListener('click', close);
    });
  }

  // ------- Mega menu
  function initMegaMenu() {
    const trigger = document.querySelector('[data-mega-trigger]');
    const panel = document.querySelector('[data-mega-panel]');
    if (!trigger || !panel) return;
    let hideTimer;
    const show = () => {
      clearTimeout(hideTimer);
      panel.classList.add('is-open');
    };
    const hide = () => {
      hideTimer = setTimeout(() => panel.classList.remove('is-open'), 200);
    };
    trigger.addEventListener('mouseenter', show);
    trigger.addEventListener('mouseleave', hide);
    panel.addEventListener('mouseenter', show);
    panel.addEventListener('mouseleave', hide);
    trigger.addEventListener('focus', show);
  }

  // ------- Cairo time clock (footer)
  function initClock() {
    const el = document.querySelector('[data-cairo-clock]');
    if (!el) return;
    const tick = () => {
      try {
        const t = new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Africa/Cairo',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }).format(new Date());
        el.textContent = `Cairo · ${t}`;
      } catch (e) {
        /* ignore */
      }
    };
    tick();
    setInterval(tick, 30000);
  }

  // ------- Page-to-page curtain transition (snappy: 150ms)
  function initCurtain() {
    if (reduceMotion) return;
    const curtain = document.querySelector('[data-curtain]');
    if (!curtain) return;
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href]');
      if (!a) return;
      // Let the browser handle modifier / non-primary clicks (open in new tab/window, downloads, etc.)
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      const href = a.getAttribute('href');
      const target = a.getAttribute('target');
      if (!href || target === '_blank') return;
      if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
      const url = new URL(href, location.href);
      if (url.origin !== location.origin) return;
      if (a.hasAttribute('data-no-curtain') || a.closest('[data-no-curtain]')) return;
      if (a.closest('form')) return;
      e.preventDefault();
      curtain.classList.add('is-in');
      setTimeout(() => {
        location.href = href;
      }, 150);
    });
  }

  // ------- Reset stuck overlays on bfcache restore (Back/Forward navigation)
  // The browser restores the page from its back/forward cache WITHOUT re-running JS,
  // so a curtain/drawer/menu left 'open' (and the body scroll-lock) would persist and
  // leave the customer on a blank or frozen page. Clear that state on pageshow.
  function initBfcacheReset() {
    window.addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      const curtain = document.querySelector('[data-curtain]');
      if (curtain) curtain.classList.remove('is-in');
      document.querySelectorAll('[data-cart-drawer], [data-mobile-menu]').forEach((el) => {
        el.classList.remove('is-open');
        el.setAttribute('aria-hidden', 'true');
      });
      document.querySelectorAll('[data-mobile-menu] details[open]').forEach((d) => d.removeAttribute('open'));
      document.querySelectorAll('[data-cart-backdrop], [data-mobile-menu-backdrop]').forEach((el) => {
        el.classList.remove('is-on');
      });
      const preloader = document.querySelector('[data-preloader]');
      if (preloader) preloader.style.display = 'none';
      document.body.style.overflow = '';
    });
  }

  // ------- Init
  // Each feature runs inside safe() so one broken effect can NEVER stop the rest
  // (e.g. a failing reel must not prevent the mobile menu or the bfcache reset from wiring up).
  function safe(fn) { try { fn(); } catch (e) { console.error('motion.js:', (fn && fn.name) || 'init', e); } }
  function init() {
    [initHeader, initReveal, initHeadline, initParallax, initReel, initHeroVideo,
     initMobileMenu, initMegaMenu, initClock, initCurtain, initLenis, initBfcacheReset].forEach(safe);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
